import { FC } from 'react';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import Head from 'next/head';
import { IPageSchemaData } from 'src/core/types';
import { fromStaticFolder } from 'src/helpers/url';
import { generateFAQSchema, generateProductSchema } from './utils';

interface ISchemaProps {
  data: IPageSchemaData;
}
const BELONG_NAME = 'Belong';

const JsonLdSchema: FC<ISchemaProps> = ({ data }) => {
  if (!data || !data.siteUrl || !data.pageUrl) {
    return null;
  }

  const {
    siteUrl,
    pageUrl,
    pathName,
    title,
    datePublished,
    dateModified,
    socialLinks = [],
    description,
    breadcrumbs = [],
    faqs = [],
    plans = [],
    sectionNbnPlanSummary
  } = data;

  const socialLinksUrls = socialLinks.map(link => link.href);
  const baseGraph: Record<string, unknown>[] = [];

  baseGraph.push({
    '@type': 'WebSite',
    name: BELONG_NAME,
    url: siteUrl,
    '@id': `${siteUrl}#website`,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteUrl}/go/search?query={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  });

  baseGraph.push({
    '@type': 'Organization',
    '@id': `${siteUrl}#org`,
    name: BELONG_NAME,
    url: siteUrl,
    logo: `${siteUrl}${fromStaticFolder('logo.png')}`,
    sameAs: socialLinksUrls
  });

  baseGraph.push({
    '@type': 'WebPage',
    '@id': `${pageUrl}#webpage`,
    url: pageUrl,
    name: title,
    description: description ? documentToPlainTextString(description) : undefined,
    inLanguage: 'en-AU',
    datePublished,
    dateModified,
    isPartOf: { '@id': `${siteUrl}#website` }
  });

  // if there are breadcrumbs, add them to the schema
  if (breadcrumbs.length > 0) {
    baseGraph.push({
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((breadcrumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: breadcrumb.label,
        item: breadcrumb.href ? `${siteUrl}${breadcrumb.href}` : pageUrl
      }))
    });
  }

  // If there are plans, add them to the schema
  if (plans.length > 0) {
    baseGraph.push(...generateProductSchema({ sectionNbnPlanSummary, pageUrl, plans, siteUrl }));
  }

  // If there are FAQs, add them to the schema
  if (faqs.length > 0) {
    baseGraph.push(generateFAQSchema(faqs, siteUrl));
  }

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': baseGraph
  };

  // Create unique ID based on current pathname
  const uniqueId = `${pathName}-schema`;

  return (
    <Head>
      <script
        id={uniqueId}
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
    </Head>
  );
};

JsonLdSchema.displayName = 'JsonLdSchema';

export { JsonLdSchema };
